/**
 * 아이템에 적용되는 Animation 타입 정의
 * Animation은 비디오/이미지 아이템에 적용되어 재생 시 시각적 효과를 제공합니다.
 *
 * 주: ANIMATION_META 실제 값은 remotion-free 모듈 animation-meta.ts 에 있고 여기서 re-export 한다
 * (서버 hydrate 라우트에서 editor_doc 매퍼가 remotion 로드 없이 참조하기 위함).
 */

import { Easing } from 'remotion';
import { ANIMATION_META } from './animation-meta';

// Easing 함수 타입 (Remotion의 interpolate가 받는 easing 타입)
export type EasingFunction = (t: number) => number;

// 미리 정의된 Easing 프리셋
export const EASING_PRESETS = {
  linear: Easing.linear,
  ease: Easing.ease,
  easeIn: Easing.in(Easing.ease),
  easeOut: Easing.out(Easing.ease),
  easeInOut: Easing.inOut(Easing.ease),
  easeInQuad: Easing.in(Easing.quad),
  easeOutQuad: Easing.out(Easing.quad),
  easeInOutQuad: Easing.inOut(Easing.quad),
  easeInCubic: Easing.in(Easing.cubic),
  easeOutCubic: Easing.out(Easing.cubic),
  easeInOutCubic: Easing.inOut(Easing.cubic),
  bounce: Easing.bounce,
  elastic: Easing.elastic(1),
} as const;

export type EasingPresetKey = keyof typeof EASING_PRESETS;

/**
 * 애니 곡선 프리셋 — "값(keyframe)" 이 아니라 "그 값까지 어떻게 가는가".
 *
 * EASING_PRESETS 와 달리 **키로 저장**한다(ItemAnimation.easing). customEasings 는 함수라
 * JSON 직렬화가 안 돼 프로젝트 저장에 실려나가지 못한다 — 사용자가 UI 로 고른 곡선은 반드시
 * 키여야 한다. 우선 4종으로 시작한다(과하면 고르기만 어려워진다).
 */
export const ANIMATION_EASINGS = {
  // 등속 — 드리프트(kenBurns)처럼 일정한 속도가 자연스러운 것
  linear: Easing.linear,
  // 감속 — 빠르게 나갔다 부드럽게 멈춤(대부분의 등장)
  out: Easing.bezier(0.22, 1, 0.36, 1),
  // 가속 후 감속 — 이동·강조
  inOut: Easing.bezier(0.65, 0, 0.35, 1),
  // 튕김 — 끝값을 살짝 넘겼다 끝값으로 안착(팝). cubic-bezier 는 끝점이 (1,1) 로 고정이라
  // "끝값 오버슈트" 까지만 되고 시작값으로는 못 돌아온다.
  overshoot: Easing.bezier(0.34, 1.56, 0.64, 1),
  // 왕복 — 끝값까지 갔다가 시작값으로 되돌아온다(강조·펄스). 0 → 1 → 0 이라 cubic-bezier 로는
  // 표현 불가(끝점 고정). easing 은 임의 함수라 sin 산 곡선으로 만든다 — 이 덕에 zoom1/zoom2/
  // pulse 가 중간 keyframe 없이 2점(시작·끝)만으로 왕복한다.
  roundTrip: (t: number) => Math.sin(Math.PI * t),
  // 감쇠 진동 — 3회 왕복하며 진폭이 0 으로 죽는다(sin(6πt)·(1−t)). 충격·흔들림 강조용.
  // roundTrip 과 같은 원리로 왕복을 곡선이 만들어 keyframe 2개(0→진폭)로 진동을 표현한다.
  shake: (t: number) => Math.sin(6 * Math.PI * t) * (1 - t),
} as const;

export type AnimationEasingKey = keyof typeof ANIMATION_EASINGS;

export const ANIMATION_EASING_KEYS = Object.keys(
  ANIMATION_EASINGS,
) as AnimationEasingKey[];

// Animation 카테고리
export type AnimationCategory = 'in' | 'out' | 'combo';

// Animation이 조작할 수 있는 속성들
export type AnimationProperty =
  | 'opacity'
  | 'scale'
  | 'rotation'
  | 'translateX'
  | 'translateY'
  // 캔버스 px 단위 이동 — translateX/Y(아이템 크기 %)와 별개 속성으로 CSS translate 에
  // 합성된다. 정확한 거리 이동(스크롤 느낌 등)용.
  | 'translateXPx'
  | 'translateYPx'
  // wipe(마스크 reveal)용 inset 각 변의 가림 비율 %. 0 = 안 가림, 100 = 전부 가림.
  | 'clipLeft'
  | 'clipRight'
  | 'clipTop'
  | 'clipBottom'
  // filter: blur(Npx) — 등장 시 초점이 맞는 느낌(blur-in 계열)용.
  | 'blur';

// 단일 속성 애니메이션 정의
export interface AnimationPropertyAnimation {
  property: AnimationProperty;
  inputRange: number[]; // 프레임 기준 keyframes (예: [0, 15] 또는 [0, 15, 30])
  // 해당 keyframe에서의 값 (예: [0, 1] 또는 [1, 0.5, 1]).
  // translateX/translateY 는 아이템 자기 크기 기준 % (`translate(x%, y%)`) — 캔버스 px 아님.
  outputRange: number[];
  easing?: AnimationEasingKey; // easing 키 (ANIMATION_EASINGS 의 키; 렌더 시 함수로 해소)
}

// Animation 메타데이터 (여러 속성 지원)
export interface AnimationMeta {
  category: AnimationCategory;
  properties: AnimationPropertyAnimation[];
  name: string;
  defaultEasing?: AnimationEasingKey; // 메타데이터 레벨의 기본 easing 키
  // 애니메이션 패널에 노출하지 않는 프로그램 전용 타입 (기본값: false)
  hidden?: boolean;
}

// Animation 타입
export type AnimationType =
  | 'fadeIn'
  | 'zoomIn'
  | 'fadeOut'
  | 'zoomOut'
  | 'zoom1'
  | 'zoom2'
  | 'slideInLeft'
  | 'slideInRight'
  | 'slideInTop'
  | 'slideInBottom'
  | 'slideOutLeft'
  | 'slideOutRight'
  | 'slideOutTop'
  | 'slideOutBottom'
  | 'wipeInLeft'
  | 'wipeInRight'
  | 'wipeInTop'
  | 'wipeInBottom'
  | 'wipeOutLeft'
  | 'wipeOutRight'
  | 'wipeOutTop'
  | 'wipeOutBottom'
  | 'popIn'
  | 'kenBurns'
  | 'pulse'
  | 'blurIn'
  | 'shakeX'
  | 'rotateIn'
  | 'rotateDrift'
  | 'riseIn';

// 아이템에 저장되는 Animation 정보
export interface ItemAnimation {
  /** Authored effect window retained when the owning clip is cut or trimmed. */
  timelineWindow?: {startInSeconds:number;endInSeconds:number};
  type: AnimationType;
  durationInSeconds: number;
  // Inspector에서 수정한 inputRange (optional, 없으면 기본값 사용)
  customInputRanges?: Partial<Record<AnimationProperty, number[]>>;
  // 이동량·배율 등 실제 값을 재정의 (optional, 없으면 메타의 outputRange 사용).
  // inputRange 와 길이가 다르면 무시하고 기본값으로 폴백한다 (interpolate throw 방지).
  // translateX/translateY 단위는 아이템 자기 크기 기준 % — 캔버스 px 아님.
  customOutputRanges?: Partial<Record<AnimationProperty, number[]>>;
  // 애니메이션 활성화 여부 (기본값: true)
  enabled?: boolean;
  // Combo 타입용: 애니메이션 시작 시점 (초, 기본값: 0)
  startTimeInSeconds?: number;
  // Combo 타입용: 애니메이션 끝 시점 (초, 설정 시 durationInSeconds 대신 사용)
  endTimeInSeconds?: number;
  // 사용자가 고른 곡선(ANIMATION_EASINGS 키) — property 별로 따로 건다. popIn 처럼 한 애니가
  // 크기 + 투명도를 같이 움직이면 각 축의 곡선이 달라야 한다(크기는 튕김, 투명도는 감속 등).
  // 함수가 아니라 키라서 프로젝트 저장/복원에 그대로 실린다.
  easings?: Partial<Record<AnimationProperty, AnimationEasingKey>>;
  // 커스텀 easing (optional, property별로 설정 가능). 함수라 저장되지 않는다 —
  // remotion-gen 이 빌드 시점에 주입하는 내부 경로 전용.
  customEasings?: Partial<Record<AnimationProperty, EasingFunction>>;
}

// 기본 효과 지속 시간 (1초)
export const DEFAULT_ANIMATION_DURATION_IN_SECONDS = 1;

// 기본 translate 이동 거리 (픽셀 또는 퍼센트 기준)
// DEFAULT_SLIDE_DISTANCE 는 animation-meta.ts 로 이동(ANIMATION_META 전용, remotion-free).

// ANIMATION_META 는 remotion-free 순수 데이터로 animation-meta.ts 에 있다 — easing 은 함수가 아니라
// AnimationEasingKey 키로 저장돼, 서버 hydrate 라우트에서 editor_doc 매퍼가 remotion 로드 없이
// import 할 수 있다. 렌더 시점(calculate-animation)이 키를 ANIMATION_EASINGS 함수로 해소한다.
// 기존 소비처 호환을 위해 여기서 re-export 한다.
export { ANIMATION_META };

// 기존 호환성을 위한 ANIMATION_CONFIGS (deprecated, ANIMATION_META 사용 권장)
export const ANIMATION_CONFIGS = {
  fadeIn: {
    name: 'Fade In',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  zoomIn: {
    name: 'Zoom In',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  fadeOut: {
    name: 'Fade Out',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  zoomOut: {
    name: 'Zoom Out',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  zoom1: {
    name: 'Zoom 1',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  zoom2: {
    name: 'Zoom 2',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  slideInLeft: {
    name: 'Slide In Left',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  slideInRight: {
    name: 'Slide In Right',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  slideInTop: {
    name: 'Slide In Top',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  slideInBottom: {
    name: 'Slide In Bottom',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  slideOutLeft: {
    name: 'Slide Out Left',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  slideOutRight: {
    name: 'Slide Out Right',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  slideOutTop: {
    name: 'Slide Out Top',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  slideOutBottom: {
    name: 'Slide Out Bottom',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  wipeInLeft: {
    name: 'Wipe In Left',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  wipeInRight: {
    name: 'Wipe In Right',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  wipeInTop: {
    name: 'Wipe In Top',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  wipeInBottom: {
    name: 'Wipe In Bottom',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  wipeOutLeft: {
    name: 'Wipe Out Left',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  wipeOutRight: {
    name: 'Wipe Out Right',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  wipeOutTop: {
    name: 'Wipe Out Top',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  wipeOutBottom: {
    name: 'Wipe Out Bottom',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  popIn: {
    name: 'Pop In',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  kenBurns: {
    name: 'Ken Burns',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  pulse: {
    name: 'Pulse',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  blurIn: {
    name: 'Blur In',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  shakeX: {
    name: 'Shake X',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  rotateIn: {
    name: 'Rotate In',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  rotateDrift: {
    name: 'Rotate Drift',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
  riseIn: {
    name: 'Rise In',
    defaultDuration: DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  },
} satisfies Record<AnimationType, { name: string; defaultDuration: number }>;

// 애니가 없는 아이템용 고정 참조 — inspector 가 `item.animations ?? []` 로 매 렌더 새 배열을
// 만들면 AnimationControls 의 memo 가 항상 깨진다(애니 미보유가 가장 흔한 케이스).
export const EMPTY_ANIMATIONS: ItemAnimation[] = [];

/**
 * 그 타입의 해당 property 가 기본으로 쓰는 곡선 키. 사용자가 고르기 전에도 Inspector 가
 * "지금 뭐가 걸려 있는지" 를 보여주기 위한 역조회다(예: popIn 의 크기=튕김, pulse=왕복).
 * 메타가 ANIMATION_EASINGS 밖의 함수(엔진 기본값 등)를 쓰면 null — UI 는 "기본" 으로 표시한다.
 */
export const getDefaultEasingKey = (
  type: AnimationType,
  property: AnimationProperty,
): AnimationEasingKey | null => {
  const meta = ANIMATION_META[type] as AnimationMeta;
  const propAnim = meta.properties.find((p) => p.property === property);
  // easing 은 이제 함수가 아니라 AnimationEasingKey 키로 저장된다(remotion-free 데이터) → 그대로 반환.
  return propAnim?.easing ?? meta.defaultEasing ?? null;
};

// Animation 생성 헬퍼 함수
export const createAnimation = (
  type: AnimationType,
  durationInSeconds: number = DEFAULT_ANIMATION_DURATION_IN_SECONDS,
): ItemAnimation => ({
  type,
  durationInSeconds,
});

/**
 * 새로운 animation을 추가할 수 있는지 검증
 *
 * 규칙:
 * 1. 같은 category + 겹치는 property → 불가
 * 2. In + Out (같은 property) → 가능
 * 3. In/Out + Combo (같은 property) → 불가
 */
export const canAddAnimation = (
  existingAnimations: ItemAnimation[],
  newAnimationType: AnimationType,
): { canAdd: boolean; conflictingAnimations: ItemAnimation[] } => {
  const newMeta = ANIMATION_META[newAnimationType];
  const newProperties = new Set(newMeta.properties.map((p) => p.property));

  const conflicting = existingAnimations.filter((existing) => {
    const existingMeta = ANIMATION_META[existing.type];

    // 겹치는 property 찾기
    const existingProperties = existingMeta.properties.map((p) => p.property);
    const hasOverlappingProperty = existingProperties.some((p) =>
      newProperties.has(p),
    );

    if (!hasOverlappingProperty) {
      return false; // 겹치는 property 없으면 충돌 없음
    }

    // 겹치는 property가 있을 때:

    // 1. 같은 category면 충돌
    if (existingMeta.category === newMeta.category) {
      return true;
    }

    // 2. Combo가 포함되면 충돌 (Combo는 전체 duration 사용)
    if (existingMeta.category === 'combo' || newMeta.category === 'combo') {
      return true;
    }

    // 3. In + Out은 허용 (시간대가 다름)
    return false;
  });

  return {
    canAdd: conflicting.length === 0,
    conflictingAnimations: conflicting,
  };
};

/**
 * Animation 추가 시 충돌하는 것들을 제거하고 새 것을 추가
 */
export const addOrReplaceAnimation = (
  existingAnimations: ItemAnimation[],
  newAnimation: ItemAnimation,
): ItemAnimation[] => {
  const { conflictingAnimations } = canAddAnimation(
    existingAnimations,
    newAnimation.type,
  );
  const conflictingTypes = new Set(conflictingAnimations.map((e) => e.type));

  const filtered = existingAnimations.filter(
    (e) => !conflictingTypes.has(e.type),
  );
  return [...filtered, newAnimation];
};

/**
 * 특정 타입의 Animation 제거
 */
export const removeAnimationByType = (
  existingAnimations: ItemAnimation[],
  typeToRemove: AnimationType,
): ItemAnimation[] => {
  return existingAnimations.filter((e) => e.type !== typeToRemove);
};

/**
 * Animation의 properties에서 사용하는 모든 property 추출
 */
export const getAnimationProperties = (
  animationType: AnimationType,
): AnimationProperty[] => {
  return ANIMATION_META[animationType].properties.map((p) => p.property);
};

/**
 * 카테고리별 Animation 타입 필터링
 */
export const getAnimationsByCategory = (
  category: AnimationCategory,
): AnimationType[] => {
  return (Object.entries(ANIMATION_META) as [AnimationType, AnimationMeta][])
    .filter(([, meta]) => meta.category === category)
    .map(([type]) => type);
};
